/**
 * Phase 92 (92-31b, UI-SPEC 3.3; D-15, D-21): the pure half of the 3PL's bulk
 * "Replace blank". Turns the selected blanks plus the replacement garment's
 * blanks into the value-match rows (auto-matched by name, then code, never a
 * guess) and the pairs those matches build. No React, no reads.
 */
import type {
  BlankValueGroup,
  BlankValueMatchRow,
  BlankValueOption,
} from '@/features/product-sync/components/supply/BlankValueMatchTable';
import type { OwnSharedGarmentBlanks, ReplaceBlankPair } from '../hooks/useSharedSkus';

/** The SQL's own bound on one Replace (`previewReplaceBlankSchema`). */
export const REPLACE_MAX_PAIRS = 200;

/** One selected shared blank of the caller's own. */
export interface SharedBlankTarget {
  id: string;
  sku: string;
  size: string | null;
  color: string | null;
}

export type OwnBlank = OwnSharedGarmentBlanks['blanks'][number];
/** A value the operator picked; null = keep the current blank. */
export type Choice = { blankValue: string | null };

/** The SQL's segments of a blank code: key, size (3+ segments), colour. */
export function blankSegments(sku: string): { key: string; size: string | null; colour: string } {
  const parts = sku.split('-');
  return {
    key: parts[0] ?? '',
    size: parts.length >= 3 ? (parts[1] ?? null) : null,
    colour: parts[parts.length - 1] ?? '',
  };
}

const norm = (value: string | null | undefined) => (value ?? '').trim().toLowerCase();

/** The key one row's choice is kept under: the group and the value, case-blind. */
export function matchChoiceKey(group: BlankValueGroup, value: string | null): string {
  return `${group}:${norm(value)}`;
}

function storeValue(target: SharedBlankTarget, group: BlankValueGroup): string | null {
  const seg = blankSegments(target.sku);
  const named = group === 'colour' ? target.color : target.size;
  const fallback = group === 'colour' ? seg.colour : seg.size;
  return named?.trim() || fallback?.trim() || null;
}

/** The replacement garment's own values, as `CODE · name`, first seen first. */
export function garmentValues(blanks: OwnBlank[]): { colours: BlankValueOption[]; sizes: BlankValueOption[] } {
  const colours = new Map<string, BlankValueOption>();
  const sizes = new Map<string, BlankValueOption>();
  for (const blank of blanks) {
    if (!colours.has(blank.colourSegment)) colours.set(blank.colourSegment, { code: blank.colourSegment, name: blank.color });
    if (blank.sizeSegment && !sizes.has(blank.sizeSegment)) sizes.set(blank.sizeSegment, { code: blank.sizeSegment, name: blank.size });
  }
  return { colours: [...colours.values()], sizes: [...sizes.values()] };
}

/** Auto-match by name, then by code; never a guess (D-15). */
function autoMatch(value: string | null, options: BlankValueOption[]): string | null {
  if (value === null) return null;
  const v = norm(value);
  return options.find((o) => norm(o.name) === v)?.code ?? options.find((o) => norm(o.code) === v)?.code ?? null;
}

/** One row per distinct selected colour or size, with the choice or the auto-match. */
export function buildMatchRows(
  targets: SharedBlankTarget[],
  group: BlankValueGroup,
  options: BlankValueOption[],
  choices: Map<string, Choice>,
): BlankValueMatchRow[] {
  const rows = new Map<string, BlankValueMatchRow>();
  for (const target of targets) {
    const value = storeValue(target, group);
    const key = norm(value);
    const known = rows.get(key);
    if (known) {
      known.variantCount += 1;
      continue;
    }
    const choice = choices.get(matchChoiceKey(group, value));
    let row: BlankValueMatchRow;
    if (choice) {
      row = choice.blankValue
        ? { storeValue: value, variantCount: 1, blankValue: choice.blankValue, how: 'chosen' }
        : { storeValue: value, variantCount: 1, blankValue: null, how: 'left_out' };
    } else {
      const auto = autoMatch(value, options);
      row = auto
        ? { storeValue: value, variantCount: 1, blankValue: auto, how: 'auto' }
        : { storeValue: value, variantCount: 1, blankValue: null, how: 'unmatched' };
    }
    rows.set(key, row);
  }
  return [...rows.values()];
}

export type ReplacePlan =
  | { kind: 'incomplete' }
  | { kind: 'ready'; pairs: ReplaceBlankPair[]; gaps: SharedBlankTarget[] };

/**
 * The pairs the matches build. Any unmatched row: `incomplete` (no review).
 * A kept row sends no pair; a size and colour the garment lacks is a gap
 * (listed, no pair); a blank already on its own match sends nothing.
 */
export function buildReplacePlan(
  targets: SharedBlankTarget[],
  blanks: OwnBlank[],
  colourRows: BlankValueMatchRow[],
  sizeRows: BlankValueMatchRow[],
  useSizes: boolean,
): ReplacePlan {
  const byKey = (rows: BlankValueMatchRow[]) => new Map(rows.map((r) => [norm(r.storeValue), r]));
  const colours = byKey(colourRows);
  const sizes = byKey(sizeRows);
  if (colourRows.some((r) => r.how === 'unmatched') || (useSizes && sizeRows.some((r) => r.how === 'unmatched'))) {
    return { kind: 'incomplete' };
  }
  const pairs: ReplaceBlankPair[] = [];
  const gaps: SharedBlankTarget[] = [];
  for (const target of targets) {
    const colour = colours.get(norm(storeValue(target, 'colour')));
    const size = useSizes ? sizes.get(norm(storeValue(target, 'size'))) : undefined;
    if (!colour?.blankValue || (useSizes && !size?.blankValue)) continue; // kept
    const to = blanks.find(
      (b) => b.colourSegment === colour.blankValue && (!useSizes || b.sizeSegment === size?.blankValue),
    );
    if (!to) gaps.push(target);
    else if (to.skuId !== target.id) pairs.push({ fromSkuId: target.id, toSkuId: to.skuId });
  }
  return { kind: 'ready', pairs, gaps };
}
